import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as programTestsService from "../services/programTests";
import * as programsService from "../services/programs";
import ExerciseRunner from "../components/tests/ExerciseRunner";
import "./ProgramTestEditor.css";
import "./ProgramTestTake.css";

const EXERCISE_TYPES = [
  { value: "stimulus", label: "Stimulus / Passage" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "true_false", label: "True / False" },
  { value: "fill_blanks", label: "Fill in the blanks" },
  { value: "match_pairs", label: "Match (pairs)" },
  { value: "connect_arrow", label: "Connect (arrows)" },
  { value: "select_words", label: "Circle / Highlight words" },
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "essay", label: "Essay (manual grading)" },
  { value: "task", label: "Task / Oral / Practical" },
  { value: "diagram_label", label: "Diagram label" },
  { value: "image_label", label: "Image label (hotspots)" },
  { value: "drag_drop", label: "Drag & drop" },
  { value: "data_interpretation", label: "Data interpretation (composite)" },
];

// Question types shown first in the "add a question" picker; the rest are
// behind "More types".
const COMMON_EXERCISE_TYPES = [
  { value: "multiple_choice", icon: "fa-list-check" },
  { value: "true_false", icon: "fa-circle-half-stroke" },
  { value: "short_text", icon: "fa-pen" },
  { value: "fill_blanks", icon: "fa-i-cursor" },
  { value: "match_pairs", icon: "fa-arrows-left-right" },
  { value: "stimulus", icon: "fa-book-open" },
  { value: "long_text", icon: "fa-align-left" },
];
const COMMON_TYPE_VALUES = new Set(COMMON_EXERCISE_TYPES.map((x) => x.value));
const EXERCISE_TYPE_ICONS = {
  connect_arrow: "fa-share-nodes",
  select_words: "fa-highlighter",
  essay: "fa-file-lines",
  task: "fa-microphone",
  diagram_label: "fa-diagram-project",
  image_label: "fa-image",
  drag_drop: "fa-up-down",
  data_interpretation: "fa-chart-column",
};

// Problems a teacher should fix before publishing (keys of
// testsModule.editor.problems.*). Types graded by hand have no answer key.
function exerciseProblems(exercise) {
  const type = String(exercise?.exercise_type || "");
  const config = exercise?.config || {};
  const problems = [];
  if (!String(exercise?.prompt || "").trim()) problems.push("missingPrompt");

  if (type === "multiple_choice") {
    const options = Array.isArray(config.options) ? config.options : [];
    if (options.length < 2) problems.push("fewOptions");
    if (options.some((o) => !String(o?.text || "").trim()))
      problems.push("emptyOption");
    const key = Array.isArray(config.answer_key) ? config.answer_key : [];
    if (!key.length) problems.push("noAnswer");
  } else if (type === "true_false") {
    if (typeof config.answer_key !== "boolean") problems.push("noAnswer");
  } else if (type === "short_text") {
    const raw = Array.isArray(config.answer_key)
      ? config.answer_key
      : config.accepted_answers;
    const accepted = Array.isArray(raw)
      ? raw.filter((x) => String(x || "").trim())
      : [];
    if (!accepted.length) problems.push("noAnswer");
  } else if (type === "fill_blanks") {
    const key =
      config.answers_by_blank_id ||
      (config.answer_key && typeof config.answer_key === "object"
        ? config.answer_key
        : {});
    const blanks = Array.isArray(config.blanks) ? config.blanks : [];
    const missing =
      !blanks.length ||
      blanks.some((b) => {
        const accepted = key[String(b?.id)];
        const list = Array.isArray(accepted) ? accepted : [accepted];
        return !list.some((x) => String(x || "").trim());
      });
    if (missing) problems.push("noAnswer");
  } else if (type === "match_pairs" || type === "connect_arrow") {
    const left = Array.isArray(config.left) ? config.left : [];
    const key =
      config.answer_key && typeof config.answer_key === "object"
        ? config.answer_key
        : {};
    if (left.length < 2) problems.push("fewPairs");
    if (left.some((l) => !String(l?.text || "").trim() || !key[String(l?.id)]))
      problems.push("noAnswer");
  } else if (type === "select_words") {
    const key = Array.isArray(config.answer_key) ? config.answer_key : [];
    if (!key.length) problems.push("noAnswer");
  }
  return problems;
}

const TEST_TYPE_OPTIONS = [
  { value: "text_comprehension", label: "Text Comprehension" },
  { value: "grammar", label: "Grammar" },
  { value: "vocabulary", label: "Vocabulary" },
  { value: "math_problems", label: "Math Problems" },
  { value: "calculations", label: "Calculations" },
  { value: "qcm", label: "QCM" },
  { value: "problem_solving", label: "Problem Solving" },
  { value: "data_interpretation", label: "Data Interpretation" },
  { value: "written_expression", label: "Written Expression" },
  { value: "mixed_test", label: "Mixed Test" },
];

function normalizeTestType(raw) {
  const v = String(raw || "").trim();
  const allowed = new Set(TEST_TYPE_OPTIONS.map((x) => x.value));
  if (allowed.has(v)) return v;
  return "mixed_test";
}

function allowedExerciseTypesForTestType(testType) {
  const t = normalizeTestType(testType);

  if (t === "written_expression") return ["long_text", "task", "essay"];
  if (t === "qcm") return ["stimulus", "multiple_choice"];
  if (t === "text_comprehension") {
    return [
      "stimulus",
      "multiple_choice",
      "true_false",
      "short_text",
      "long_text",
      "fill_blanks",
      "match_pairs",
      "select_words",
    ];
  }
  if (t === "grammar") {
    return [
      "multiple_choice",
      "true_false",
      "fill_blanks",
      "short_text",
      "select_words",
      "match_pairs",
    ];
  }
  if (t === "vocabulary") {
    return [
      "multiple_choice",
      "match_pairs",
      "select_words",
      "short_text",
      "image_label",
    ];
  }
  if (t === "calculations") {
    return ["short_text", "fill_blanks", "multiple_choice", "true_false"];
  }
  if (t === "math_problems") {
    return [
      "stimulus",
      "short_text",
      "long_text",
      "multiple_choice",
      "data_interpretation",
    ];
  }
  if (t === "problem_solving") {
    return ["stimulus", "short_text", "long_text", "multiple_choice"];
  }
  if (t === "data_interpretation") {
    return ["stimulus", "data_interpretation", "multiple_choice", "short_text"];
  }

  return EXERCISE_TYPES.map((x) => x.value);
}

function normalizeOptions(raw) {
  const options = Array.isArray(raw) ? raw : [];
  return options
    .map((opt, index) => {
      const id = String(opt?.id ?? index + 1);
      const text = String(opt?.text ?? "");
      return { id, text };
    })
    .filter((opt) => opt.id);
}

function buildDefaultConfig(type) {
  if (type === "multiple_choice") {
    return {
      allow_multiple: false,
      ui: "list",
      options: [
        { id: "A", text: "" },
        { id: "B", text: "" },
        { id: "C", text: "" },
      ],
    };
  }

  if (type === "fill_blanks") {
    return {
      template: "",
      blanks: [{ id: "1", placeholder: "..." }],
    };
  }

  if (type === "match_pairs" || type === "connect_arrow") {
    return {
      left: [
        { id: "1", text: "" },
        { id: "2", text: "" },
      ],
      right: [
        { id: "A", text: "" },
        { id: "B", text: "" },
      ],
      answer_key: { 1: "A", 2: "B" },
      ui: type === "connect_arrow" ? "arrows" : "select",
    };
  }

  if (type === "select_words") {
    return {
      mode: "circle",
      items: [
        { id: "1", text: "" },
        { id: "2", text: "" },
        { id: "3", text: "" },
      ],
    };
  }

  if (type === "true_false") return {};
  if (type === "stimulus") return {};
  if (type === "short_text") return { placeholder: "", answer_key: [] };
  if (type === "long_text") return { rows: 6 };
  if (type === "task") return { rows: 8 };
  if (type === "essay") return { rows: 10 };

  if (type === "diagram_label") {
    return {
      image_url: "",
      items: [
        { id: "1", label: "" },
        { id: "2", label: "" },
      ],
    };
  }

  if (type === "image_label") {
    return {
      image_url: "",
      hotspots: [],
    };
  }

  if (type === "drag_drop") {
    return {
      mode: "order",
      items: [
        { id: "1", text: "First" },
        { id: "2", text: "Second" },
      ],
    };
  }

  if (type === "data_interpretation") {
    return {
      title: "",
      prompt: "",
      items: [],
      questions: [],
    };
  }

  return {};
}

function normalizeIdTextItems(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      text: String(it?.text ?? it?.label ?? ""),
    }))
    .filter((it) => it.id);
}

function normalizeHotspots(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => {
      const id = String(it?.id ?? index + 1);
      const x = Number(it?.x ?? 50);
      const y = Number(it?.y ?? 50);
      const w = Number(it?.w ?? it?.width ?? 18);
      const h = Number(it?.h ?? it?.height ?? 12);
      return {
        id,
        x: Number.isNaN(x) ? 50 : x,
        y: Number.isNaN(y) ? 50 : y,
        w: Number.isNaN(w) ? 18 : w,
        h: Number.isNaN(h) ? 12 : h,
      };
    })
    .filter((it) => it.id);
}

function clampNumber(n, min, max) {
  const v = Number(n);
  if (Number.isNaN(v)) return min;
  return Math.max(min, Math.min(max, v));
}

// Options are labelled A, B, C… by position; ids follow the labels so the
// student sees the same letters. Returns the options and an old->new id map.
function relabelOptions(options) {
  const map = {};
  const next = options.map((opt, index) => {
    const id = String.fromCharCode(65 + index);
    map[String(opt.id)] = id;
    return { ...opt, id };
  });
  return { options: next, map };
}

// Stable shuffle (same seed and length -> same order) so the right column
// of a matching question is mixed but doesn't jump around while typing.
function seededShuffle(items, seed) {
  const result = items.slice();
  let state = (Math.abs(Number(seed) || 7) % 2147483646) + 1;
  for (let i = result.length - 1; i > 0; i -= 1) {
    state = (state * 16807) % 2147483647;
    const j = state % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// "Je [vais|va] à l'école." -> template with {{1}}, blanks, answer key.
function parseBlankSentence(text) {
  const blanks = [];
  const answerKey = {};
  let index = 0;
  const template = String(text || "").replace(/\[([^\]]*)\]/g, (_, inner) => {
    index += 1;
    const id = String(index);
    const answers = inner
      .split("|")
      .map((x) => x.trim())
      .filter(Boolean);
    blanks.push({ id, placeholder: "..." });
    answerKey[id] = answers;
    return `{{${id}}}`;
  });
  return { template, blanks, answerKey };
}

// Inverse of parseBlankSentence, for questions created before source_text.
function blankSentenceFromConfig(config) {
  if (typeof config?.source_text === "string") return config.source_text;
  const key =
    config?.answers_by_blank_id ||
    (config?.answer_key && typeof config.answer_key === "object"
      ? config.answer_key
      : {});
  return String(config?.template || "").replace(/\{\{\s*([^}\s]+)\s*\}\}/g, (_, id) => {
    const answers = Array.isArray(key[id]) ? key[id] : key[id] ? [key[id]] : [];
    return `[${answers.join("|")}]`;
  });
}

function nextUniqueId(existingIds) {
  const set = new Set((existingIds || []).map(String));
  for (let i = 1; i < 5000; i += 1) {
    const candidate = String(i);
    if (!set.has(candidate)) return candidate;
  }
  return String(Date.now());
}

function ImageHotspotBuilder({ imageUrl, hotspots, onChange, tFn }) {
  const containerRef = React.useRef(null);
  const [activeId, setActiveId] = useState(null);
  const dragRef = React.useRef(null);

  const tLocal = (key, options) => {
    if (typeof tFn === "function") return tFn(key, options);
    return options?.defaultValue || key;
  };

  const getBox = () => containerRef.current?.getBoundingClientRect();

  const updateHotspot = useCallback(
    (id, patch) => {
      const next = (hotspots || []).map((h) =>
        String(h.id) === String(id) ? { ...h, ...patch } : h,
      );
      onChange(next);
    },
    [hotspots, onChange],
  );

  const addHotspotAt = (xPct, yPct) => {
    const ids = (hotspots || []).map((h) => h.id);
    const id = nextUniqueId(ids);
    const w = 18;
    const h = 12;
    const next = [
      ...(hotspots || []),
      {
        id,
        x: clampNumber(xPct, 0, 100),
        y: clampNumber(yPct, 0, 100),
        w,
        h,
      },
    ];
    onChange(next);
    setActiveId(id);
  };

  const beginDrag = (e, id, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const box = getBox();
    if (!box) return;
    const hs = (hotspots || []).find((h) => String(h.id) === String(id));
    if (!hs) return;

    setActiveId(id);
    dragRef.current = {
      id,
      mode,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: Number(hs.x),
      startY: Number(hs.y),
      startW: Number(hs.w),
      startH: Number(hs.h),
      box,
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag, { once: true });
  };

  const onPointerMove = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startClientX;
      const dy = e.clientY - d.startClientY;
      const dxPct = (dx / d.box.width) * 100;
      const dyPct = (dy / d.box.height) * 100;

      if (d.mode === "move") {
        updateHotspot(d.id, {
          x: clampNumber(d.startX + dxPct, 0, 100),
          y: clampNumber(d.startY + dyPct, 0, 100),
        });
        return;
      }
      if (d.mode === "resize") {
        updateHotspot(d.id, {
          w: clampNumber(d.startW + dxPct, 2, 100),
          h: clampNumber(d.startH + dyPct, 2, 100),
        });
      }
    },
    [updateHotspot],
  );

  const endDrag = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
  };

  useEffect(
    () => () => window.removeEventListener("pointermove", onPointerMove),
    [onPointerMove],
  );

  return (
    <div className="test-editor__hotspotBuilder">
      <div className="test-editor__hotspotToolbar">
        <button
          type="button"
          className="btn ghost"
          onClick={() => addHotspotAt(40, 40)}
        >
          {tLocal("testsModule.editor.addHotspot", {
            defaultValue: "+ Add hotspot",
          })}
        </button>
        <div className="test-editor__hint">
          {tLocal("testsModule.editor.hotspotBuilderTip", {
            defaultValue: "Tip: click the image to add a hotspot.",
          })}
        </div>
      </div>

      <div
        ref={containerRef}
        className="test-editor__hotspotStage"
        onClick={(e) => {
          if (!imageUrl) return;
          const box = getBox();
          if (!box) return;
          const x = ((e.clientX - box.left) / box.width) * 100;
          const y = ((e.clientY - box.top) / box.height) * 100;
          // Place using top-left in %, slightly centered.
          addHotspotAt(clampNumber(x - 8, 0, 100), clampNumber(y - 6, 0, 100));
        }}
        role="group"
        aria-label={tLocal("testsModule.editor.hotspotBuilderAria", {
          defaultValue: "Hotspot builder",
        })}
      >
        {imageUrl ? (
          <img
            className="test-editor__hotspotImg"
            src={imageUrl}
            alt={tLocal("testsModule.editor.hotspotAlt", {
              defaultValue: "Hotspot",
            })}
          />
        ) : null}

        {(hotspots || []).map((hs) => {
          const active = String(activeId) === String(hs.id);
          return (
            <div
              key={hs.id}
              className={
                active
                  ? "test-editor__hotspotBox test-editor__hotspotBox--active"
                  : "test-editor__hotspotBox"
              }
              style={{
                left: `${hs.x}%`,
                top: `${hs.y}%`,
                width: `${hs.w}%`,
                height: `${hs.h}%`,
              }}
              onPointerDown={(e) => beginDrag(e, hs.id, "move")}
              role="button"
              tabIndex={0}
              aria-label={tLocal("testsModule.runner.hotspotLabel", {
                id: hs.id,
                defaultValue: `Hotspot ${hs.id}`,
              })}
              onKeyDown={(e) => {
                if (e.key === "Delete" || e.key === "Backspace") {
                  e.preventDefault();
                  onChange(
                    (hotspots || []).filter(
                      (x) => String(x.id) !== String(hs.id),
                    ),
                  );
                  setActiveId(null);
                }
              }}
            >
              <span className="test-editor__hotspotTag">{hs.id}</span>
              <span
                className="test-editor__hotspotHandle"
                onPointerDown={(e) => beginDrag(e, hs.id, "resize")}
                role="button"
                aria-label={tLocal("testsModule.editor.resizeHotspotLabel", {
                  id: hs.id,
                  defaultValue: `Resize hotspot ${hs.id}`,
                })}
                tabIndex={-1}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConnectArrowBuilder({
  left,
  right,
  answerKey,
  allowManyToOne,
  onChangeAnswerKey,
  tFn,
}) {
  const containerRef = React.useRef(null);
  const leftRefs = React.useRef({});
  const rightRefs = React.useRef({});
  const [activeLeft, setActiveLeft] = useState(null);
  const [layoutTick, setLayoutTick] = useState(0);

  const tLocal = (key, options) => {
    if (typeof tFn === "function") return tFn(key, options);
    return options?.defaultValue || key;
  };

  useEffect(() => {
    const onResize = () => setLayoutTick((x) => x + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const box = containerRef.current?.getBoundingClientRect();
  const anchor = (el) => {
    if (!el || !box) return null;
    const r = el.getBoundingClientRect();
    return {
      x: r.left - box.left + r.width / 2,
      y: r.top - box.top + r.height / 2,
    };
  };

  const pairs = answerKey && typeof answerKey === "object" ? answerKey : {};
  const lines = [];
  for (const [l, r] of Object.entries(pairs)) {
    if (!r) continue;
    const a = anchor(leftRefs.current[l]);
    const b = anchor(rightRefs.current[r]);
    if (!a || !b) continue;
    lines.push({ l, r, a, b });
  }

  const connect = (lId, rId) => {
    if (!lId || !rId) return;
    const next = { ...(pairs || {}) };
    if (!allowManyToOne) {
      for (const [l, r] of Object.entries(next)) {
        if (r === rId && l !== lId) delete next[l];
      }
    }
    next[String(lId)] = String(rId);
    onChangeAnswerKey(next);
    setActiveLeft(null);
  };

  return (
    <div className="test-editor__connectBuilder">
      <div className="test-editor__hint">
        Click a left item, then click its correct match on the right.
      </div>
      <div className="test-editor__connectStage" ref={containerRef}>
        <svg className="test-editor__connectSvg" aria-hidden="true">
          <defs>
            <marker
              id="editor-arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="5"
              orient="auto"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill="rgba(30, 100, 218, 0.85)" />
            </marker>
          </defs>
          {lines.map((ln) => (
            <line
              key={`${ln.l}-${ln.r}-${layoutTick}`}
              x1={ln.a.x}
              y1={ln.a.y}
              x2={ln.b.x}
              y2={ln.b.y}
              className="test-editor__connectLine"
              markerEnd="url(#editor-arrowhead)"
            />
          ))}
        </svg>

        <div className="test-editor__connectCol">
          {left.map((it) => {
            const active = activeLeft === it.id;
            return (
              <button
                key={it.id}
                type="button"
                ref={(el) => {
                  if (el) leftRefs.current[it.id] = el;
                }}
                className={
                  active
                    ? "test-editor__connectPick test-editor__connectPick--active"
                    : "test-editor__connectPick"
                }
                aria-pressed={active}
                onClick={() => setActiveLeft(active ? null : it.id)}
              >
                <span className="test-editor__connectBadge">{it.id}</span>
                <span>{it.text || it.id}</span>
              </button>
            );
          })}
        </div>

        <div className="test-editor__connectCol test-editor__connectCol--right">
          {right.map((it) => (
            <button
              key={it.id}
              type="button"
              ref={(el) => {
                if (el) rightRefs.current[it.id] = el;
              }}
              className="test-editor__connectPick test-editor__connectPick--right"
              onClick={() => {
                if (!activeLeft) return;
                connect(activeLeft, it.id);
              }}
            >
              <span className="test-editor__connectBadge test-editor__connectBadge--right">
                {it.id}
              </span>
              <span>{it.text || it.id}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="test-editor__connectActions">
        <button
          type="button"
          className="btn ghost"
          onClick={() => {
            onChangeAnswerKey({});
            setActiveLeft(null);
          }}
        >
          {tLocal("testsModule.editor.clearAllConnections", {
            defaultValue: "Clear all connections",
          })}
        </button>
      </div>
    </div>
  );
}

function ExerciseLivePreview({ exercise, language }) {
  const [value, setValue] = useState({});

  useEffect(() => {
    setValue({});
  }, [exercise?.id, exercise?.exercise_type]);

  return (
    <div className="test-editor__previewBody">
      <ExerciseRunner
        prefix="test-take"
        language={language}
        exercise={exercise}
        value={value}
        onChange={setValue}
      />
    </div>
  );
}

function normalizePairs(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      text: String(it?.text ?? ""),
      image_url: String(it?.image_url ?? ""),
    }))
    .filter((it) => it.id);
}

function normalizeWordItems(raw) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, index) => ({
      id: String(it?.id ?? index + 1),
      text: String(it?.text ?? ""),
    }))
    .filter((it) => it.id);
}

function ExerciseConfigEditor({ exercise, onChange }) {
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const tFn = (key, options) => {
    try {
      return t(key, options);
    } catch {
      return options?.defaultValue || key;
    }
  };

  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadErr, setUploadErr] = useState("");

  const typeRaw = exercise.exercise_type;
  const type = typeRaw === "connect_arrow" ? "match_pairs" : typeRaw;
  const config = exercise.config || {};

  const grading =
    config.grading && typeof config.grading === "object" ? config.grading : {};
  const setGrading = (patch) =>
    onChange({
      ...exercise,
      config: {
        ...config,
        grading: {
          ...grading,
          ...patch,
        },
      },
    });

  const negativePerWrong =
    grading.negative_per_wrong ?? grading.negative_marking ?? "";

  const gradingFields = (
    <>
      <label className="test-editor__inline">
        <input
          type="checkbox"
          checked={grading.partial_credit !== false}
          onChange={(e) => setGrading({ partial_credit: e.target.checked })}
        />
        <span>
          {tFn("testsModule.editor.allowPartialCredit", {
            defaultValue: "Allow partial credit",
          })}
        </span>
      </label>

      <label>
        <span>
          {tFn("testsModule.editor.negativePerWrongOptional", {
            defaultValue: "Negative per wrong (optional)",
          })}
        </span>
        <input
          type="number"
          step="0.25"
          min="0"
          value={String(negativePerWrong ?? "")}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "") {
              const { negative_per_wrong, negative_marking, ...rest } = grading;
              onChange({
                ...exercise,
                config: {
                  ...config,
                  grading: rest,
                },
              });
              return;
            }
            setGrading({ negative_per_wrong: Number(next) });
          }}
        />
      </label>

      {type === "short_text" ||
      type === "fill_blanks" ||
      type === "diagram_label" ? (
        <label className="test-editor__inline">
          <input
            type="checkbox"
            checked={grading.case_insensitive !== false}
            onChange={(e) => setGrading({ case_insensitive: e.target.checked })}
          />
          <span>
            {tFn("testsModule.editor.caseInsensitiveMatching", {
              defaultValue: "Case-insensitive matching",
            })}
          </span>
        </label>
      ) : null}
    </>
  );

  // Rarely needed settings, collapsed by default to keep the editor simple.
  const renderAdvanced = (extra = null) => (
    <details className="test-editor__advanced">
      <summary>
        {tFn("testsModule.editor.advancedOptions", {
          defaultValue: "Advanced options",
        })}
      </summary>
      <div className="test-editor__config">
        {extra}
        {gradingFields}
      </div>
    </details>
  );
  const gradingControls = renderAdvanced();

  if (type === "multiple_choice") {
    const allowMultiple = Boolean(config.allow_multiple);
    const ui = String(config.ui || "list");
    const options = normalizeOptions(config.options);
    const answerKey = Array.isArray(config.answer_key)
      ? config.answer_key.map(String)
      : [];
    const setConfig = (patch) =>
      onChange({ ...exercise, config: { ...config, ...patch } });

    const toggleCorrect = (id) => {
      if (allowMultiple) {
        setConfig({
          answer_key: answerKey.includes(id)
            ? answerKey.filter((x) => x !== id)
            : [...answerKey, id],
        });
      } else {
        setConfig({ answer_key: [id] });
      }
    };

    const writeOptions = (nextOptions, nextKey = answerKey) => {
      const { options: relabelled, map } = relabelOptions(nextOptions);
      setConfig({
        options: relabelled,
        answer_key: nextKey.map((id) => map[id]).filter(Boolean),
      });
    };

    const presentation = (
      <label>
        <span>
          {tFn("testsModule.editor.presentation", {
            defaultValue: "Presentation",
          })}
        </span>
        <select value={ui} onChange={(e) => setConfig({ ui: e.target.value })}>
          <option value="list">
            {tFn("testsModule.editor.presentationList", {
              defaultValue: "List",
            })}
          </option>
          <option value="underline">
            {tFn("testsModule.editor.presentationUnderline", {
              defaultValue: "Underline (paper-like)",
            })}
          </option>
        </select>
      </label>
    );

    return (
      <div className="test-editor__config">
        <p className="test-editor__hint">
          {allowMultiple
            ? tFn("testsModule.editor.mcqHintMultiple", {
                defaultValue: "Write the choices and tick every correct answer.",
              })
            : tFn("testsModule.editor.mcqHint", {
                defaultValue: "Write the choices and tick the correct answer.",
              })}
        </p>
        <div className="test-editor__choices">
          {options.map((opt) => {
            const on = answerKey.includes(opt.id);
            return (
              <div
                key={opt.id}
                className={`test-editor__choice${on ? " is-correct" : ""}`}
              >
                <label
                  className="test-editor__choiceMark"
                  title={tFn("testsModule.editor.markCorrect", {
                    defaultValue: "Correct answer",
                  })}
                >
                  <input
                    type={allowMultiple ? "checkbox" : "radio"}
                    name={`ak-${exercise.id}`}
                    checked={on}
                    onChange={() => toggleCorrect(opt.id)}
                  />
                  <span className="sr-only">
                    {tFn("testsModule.editor.markCorrect", {
                      defaultValue: "Correct answer",
                    })}
                  </span>
                </label>
                <span className="test-editor__choiceLetter">{opt.id}</span>
                <input
                  className="test-editor__choiceText"
                  placeholder={tFn("testsModule.editor.optionTextPlaceholder", {
                    defaultValue: "Option text",
                  })}
                  value={opt.text}
                  onChange={(e) =>
                    setConfig({
                      options: options.map((o) =>
                        o.id === opt.id ? { ...o, text: e.target.value } : o,
                      ),
                    })
                  }
                />
                {on ? (
                  <span className="test-editor__correctTag">
                    {tFn("testsModule.editor.correctTag", {
                      defaultValue: "Correct",
                    })}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="test-editor__iconBtn"
                  onClick={() =>
                    writeOptions(
                      options.filter((o) => o.id !== opt.id),
                      answerKey.filter((x) => x !== opt.id),
                    )
                  }
                  disabled={options.length <= 2}
                  aria-label={tFn("testsModule.editor.removeOption", {
                    defaultValue: "Remove option",
                  })}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="test-editor__rowActions">
          <button
            type="button"
            className="btn ghost"
            onClick={() => writeOptions([...options, { id: "_new", text: "" }])}
          >
            {tFn("testsModule.editor.addOption", {
              defaultValue: "+ Add option",
            })}
          </button>
          <label className="test-editor__inline">
            <input
              type="checkbox"
              checked={allowMultiple}
              onChange={(e) =>
                setConfig({
                  allow_multiple: e.target.checked,
                  // Single choice keeps only the first ticked answer.
                  answer_key: e.target.checked
                    ? answerKey
                    : answerKey.slice(0, 1),
                })
              }
            />
            <span>
              {tFn("testsModule.editor.allowMultipleAnswers", {
                defaultValue: "Allow multiple answers",
              })}
            </span>
          </label>
        </div>

        {renderAdvanced(presentation)}
      </div>
    );
  }

  if (typeRaw === "true_false") {
    const answerKey =
      typeof config.answer_key === "boolean" ? config.answer_key : null;
    return (
      <div className="test-editor__config">
        <h4>
          {tFn("testsModule.editor.answerKey", { defaultValue: "Answer key" })}
        </h4>
        <label className="test-editor__inline">
          <input
            type="radio"
            name={`tf-${exercise.id}`}
            checked={answerKey === true}
            onChange={() =>
              onChange({ ...exercise, config: { ...config, answer_key: true } })
            }
          />
          <span>
            {tFn("testsModule.runner.true", { defaultValue: "True" })}
          </span>
        </label>
        <label className="test-editor__inline">
          <input
            type="radio"
            name={`tf-${exercise.id}`}
            checked={answerKey === false}
            onChange={() =>
              onChange({
                ...exercise,
                config: { ...config, answer_key: false },
              })
            }
          />
          <span>
            {tFn("testsModule.runner.false", { defaultValue: "False" })}
          </span>
        </label>

        {gradingControls}
      </div>
    );
  }

  if (type === "fill_blanks") {
    const sentence = blankSentenceFromConfig(config);
    const { blanks } = parseBlankSentence(sentence);
    return (
      <div className="test-editor__config">
        <label className="test-editor__full">
          <span>
            {tFn("testsModule.editor.blankSentenceLabel", {
              defaultValue: "Sentence",
            })}
          </span>
          <textarea
            rows={4}
            value={sentence}
            placeholder={tFn("testsModule.editor.blankSentencePlaceholder", {
              defaultValue: "Je [vais] à l'école tous les [matins|jours].",
            })}
            onChange={(e) => {
              const parsed = parseBlankSentence(e.target.value);
              const { answers_by_blank_id, ...rest } = config;
              onChange({
                ...exercise,
                config: {
                  ...rest,
                  source_text: e.target.value,
                  template: parsed.template,
                  blanks: parsed.blanks,
                  answer_key: parsed.answerKey,
                },
              });
            }}
          />
        </label>
        <p className="test-editor__hint">
          {tFn("testsModule.editor.blankSentenceHint", {
            defaultValue:
              "Put each word to find between [brackets]. Several accepted answers: [vais|va].",
          })}
        </p>
        <p className="test-editor__hint">
          {tFn("testsModule.editor.blankCount", {
            defaultValue: "{{n}} blank(s) found.",
            n: blanks.length,
          })}
        </p>
        {gradingControls}
      </div>
    );
  }

  if (typeRaw === "match_pairs") {
    const left = normalizeIdTextItems(config.left);
    const right = normalizeIdTextItems(config.right);
    const key =
      config.answer_key && typeof config.answer_key === "object"
        ? config.answer_key
        : {};
    const linkedRight = new Set(Object.values(key).map(String));
    const rows = left.map((l) => ({
      left: l.text,
      right: right.find((r) => r.id === String(key[l.id] ?? ""))?.text || "",
    }));
    // Right-hand items no pair points to: kept as decoys.
    const decoys = right.filter((r) => !linkedRight.has(r.id)).map((r) => r.text);

    const writeRows = (nextRows) => {
      const nextLeft = nextRows.map((row, i) => ({
        id: String(i + 1),
        text: row.left,
      }));
      const rightItems = [
        ...nextRows.map((row, i) => ({ pair: String(i + 1), text: row.right })),
        ...decoys.map((text) => ({ pair: null, text })),
      ];
      const shuffled = seededShuffle(rightItems, exercise.id);
      const nextRight = [];
      const nextKey = {};
      shuffled.forEach((item, i) => {
        const id = String.fromCharCode(65 + i);
        nextRight.push({ id, text: item.text });
        if (item.pair) nextKey[item.pair] = id;
      });
      onChange({
        ...exercise,
        config: { ...config, left: nextLeft, right: nextRight, answer_key: nextKey },
      });
    };

    return (
      <div className="test-editor__config">
        <p className="test-editor__hint">
          {tFn("testsModule.editor.pairsHint", {
            defaultValue:
              "Write each pair on one line. Students will see the right column shuffled.",
          })}
        </p>
        <div className="test-editor__pairs">
          {rows.map((row, index) => (
            <div key={index} className="test-editor__pair">
              <input
                value={row.left}
                placeholder={tFn("testsModule.editor.pairLeft", {
                  defaultValue: "Item",
                })}
                onChange={(e) =>
                  writeRows(
                    rows.map((r, i) =>
                      i === index ? { ...r, left: e.target.value } : r,
                    ),
                  )
                }
              />
              <i className="fa-solid fa-arrows-left-right" aria-hidden="true" />
              <input
                value={row.right}
                placeholder={tFn("testsModule.editor.pairRight", {
                  defaultValue: "Its match",
                })}
                onChange={(e) =>
                  writeRows(
                    rows.map((r, i) =>
                      i === index ? { ...r, right: e.target.value } : r,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="test-editor__iconBtn"
                onClick={() => writeRows(rows.filter((_, i) => i !== index))}
                disabled={rows.length <= 2}
                aria-label={tFn("testsModule.editor.removePair", {
                  defaultValue: "Remove pair",
                })}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
        <div className="test-editor__rowActions">
          <button
            type="button"
            className="btn ghost"
            onClick={() => writeRows([...rows, { left: "", right: "" }])}
          >
            {tFn("testsModule.editor.addPair", { defaultValue: "+ Add pair" })}
          </button>
        </div>
        {gradingControls}
      </div>
    );
  }

  if (type === "match_pairs") {
    const left = normalizePairs(config.left);
    const right = normalizePairs(config.right);
    const ui = String(config.ui || "select");

    const behavior =
      config.behavior && typeof config.behavior === "object"
        ? config.behavior
        : {};
    const allowManyToOne = Boolean(behavior.allow_many_to_one);

    const updateSide = (side, next) => {
      onChange({
        ...exercise,
        config: {
          ...config,
          [side]: next,
        },
      });
    };

    const answerKey =
      config.answer_key &&
      typeof config.answer_key === "object" &&
      !Array.isArray(config.answer_key)
        ? config.answer_key
        : {};

    const uploadRightImage = async (idx, file) => {
      if (!file) return;
      if (!accessToken) {
        setUploadErr(
          tFn("testsModule.common.unauthorized", {
            defaultValue: "Unauthorized",
          }),
        );
        return;
      }
      setUploadErr("");
      setUploadingKey(`right-${idx}`);
      try {
        const payload = await programsService.uploadTopicImage(
          accessToken,
          file,
        );
        const url = String(payload?.url || "").trim();
        if (!url) {
          throw new Error(
            tFn("testsModule.editor.uploadFailed", {
              defaultValue: "Upload failed",
            }),
          );
        }
        const next = right.slice();
        next[idx] = { ...next[idx], image_url: url };
        updateSide("right", next);
      } catch (e) {
        setUploadErr(
          e?.message ||
            tFn("testsModule.editor.uploadFailed", {
              defaultValue: "Upload failed",
            }),
        );
      } finally {
        setUploadingKey(null);
      }
    };

    return (
      <div className="test-editor__config">
        <label>
          <span>
            {tFn("testsModule.editor.presentation", {
              defaultValue: "Presentation",
            })}
          </span>
          <select
            value={ui}
            onChange={(e) =>
              onChange({
                ...exercise,
                config: { ...config, ui: e.target.value },
              })
            }
          >
            <option value="select">
              {tFn("testsModule.editor.matchPresentationSelect", {
                defaultValue: "Select IDs (simple)",
              })}
            </option>
            <option value="arrows">
              {tFn("testsModule.editor.matchPresentationArrows", {
                defaultValue: "Arrows (paper-like)",
              })}
            </option>
          </select>
        </label>

        <label className="test-editor__inline">
          <input
            type="checkbox"
            checked={allowManyToOne}
            onChange={(e) =>
              onChange({
                ...exercise,
                config: {
                  ...config,
                  behavior: {
                    ...behavior,
                    allow_many_to_one: e.target.checked,
                  },
                },
              })
            }
          />
          <span>
            {tFn("testsModule.editor.matchAllowManyToOne", {
              defaultValue:
                "Allow many left items to connect to the same right item",
            })}
          </span>
        </label>

        <div className="test-editor__twoCols">
          <div>
            <h4>
              {tFn("testsModule.editor.leftColumn", {
                defaultValue: "Left column",
              })}
            </h4>
            <div className="test-editor__options">
              {left.map((opt, idx) => (
                <div key={opt.id} className="test-editor__pairRow">
                  <input
                    className="test-editor__optionId"
                    value={opt.id}
                    onChange={(e) => {
                      const next = left.slice();
                      next[idx] = { ...next[idx], id: e.target.value };
                      updateSide("left", next);
                    }}
                  />
                  <input
                    className="test-editor__optionText"
                    placeholder={tFn("testsModule.editor.textPlaceholder", {
                      defaultValue: "Text",
                    })}
                    value={opt.text}
                    onChange={(e) => {
                      const next = left.slice();
                      next[idx] = { ...next[idx], text: e.target.value };
                      updateSide("left", next);
                    }}
                  />
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() =>
                      updateSide(
                        "left",
                        left.filter((_, i) => i !== idx),
                      )
                    }
                    aria-label={tFn("testsModule.editor.removeLeft", {
                      defaultValue: "Remove left",
                    })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn ghost"
                onClick={() =>
                  updateSide("left", [
                    ...left,
                    { id: String(left.length + 1), text: "" },
                  ])
                }
              >
                {tFn("testsModule.editor.addLeftItem", {
                  defaultValue: "+ Add left item",
                })}
              </button>
            </div>
          </div>

          <div>
            <h4>
              {tFn("testsModule.editor.rightColumn", {
                defaultValue: "Right column",
              })}
            </h4>
            <div className="test-editor__options">
              {right.map((opt, idx) => (
                <div
                  key={opt.id}
                  className="test-editor__pairRow test-editor__pairRow--right"
                >
                  <input
                    className="test-editor__optionId"
                    value={opt.id}
                    onChange={(e) => {
                      const next = right.slice();
                      next[idx] = { ...next[idx], id: e.target.value };
                      updateSide("right", next);
                    }}
                  />
                  <input
                    className="test-editor__optionText"
                    placeholder={tFn("testsModule.editor.textPlaceholder", {
                      defaultValue: "Text",
                    })}
                    value={opt.text}
                    onChange={(e) => {
                      const next = right.slice();
                      next[idx] = { ...next[idx], text: e.target.value };
                      updateSide("right", next);
                    }}
                  />

                  <div className="test-editor__uploadCell">
                    {opt.image_url ? (
                      <img
                        className="test-editor__thumb"
                        src={opt.image_url}
                        alt={tFn("testsModule.editor.imageAlt", {
                          defaultValue: "Image",
                        })}
                      />
                    ) : null}

                    <label className="btn ghost test-editor__uploadBtn">
                      {uploadingKey === `right-${idx}`
                        ? tFn("testsModule.editor.uploading", {
                            defaultValue: "Uploading...",
                          })
                        : opt.image_url
                          ? tFn("testsModule.editor.replaceImage", {
                              defaultValue: "Replace image",
                            })
                          : tFn("testsModule.editor.imageOptional", {
                              defaultValue: "Image (optional)",
                            })}
                      <input
                        type="file"
                        accept="image/*"
                        className="test-editor__fileInput"
                        disabled={uploadingKey === `right-${idx}`}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          e.target.value = "";
                          uploadRightImage(idx, file);
                        }}
                      />
                    </label>

                    {opt.image_url ? (
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => {
                          const next = right.slice();
                          next[idx] = { ...next[idx], image_url: "" };
                          updateSide("right", next);
                        }}
                        disabled={uploadingKey === `right-${idx}`}
                      >
                        {tFn("testsModule.common.remove", {
                          defaultValue: "Remove",
                        })}
                      </button>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() =>
                      updateSide(
                        "right",
                        right.filter((_, i) => i !== idx),
                      )
                    }
                    aria-label={tFn("testsModule.editor.removeRight", {
                      defaultValue: "Remove right",
                    })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn ghost"
                onClick={() =>
                  updateSide("right", [
                    ...right,
                    { id: String(right.length + 1), text: "" },
                  ])
                }
              >
                {tFn("testsModule.editor.addRightItem", {
                  defaultValue: "+ Add right item",
                })}
              </button>
            </div>
          </div>
        </div>

        {uploadErr ? (
          <p className="test-editor__hint" style={{ color: "#b00020" }}>
            {uploadErr}
          </p>
        ) : null}

        <p className="test-editor__hint">
          {tFn("testsModule.editor.arrowsModeHint", {
            defaultValue:
              '"Arrows" mode lets the student connect left to right by clicking, and draws arrows.',
          })}
        </p>

        <details className="test-editor__preview">
          <summary>
            {tFn("testsModule.editor.answerKeyBuilderArrows", {
              defaultValue: "Answer key builder (arrows)",
            })}
          </summary>
          <ConnectArrowBuilder
            left={left}
            right={right}
            answerKey={answerKey}
            allowManyToOne={allowManyToOne}
            tFn={tFn}
            onChangeAnswerKey={(nextKey) =>
              onChange({
                ...exercise,
                config: {
                  ...config,
                  answer_key: nextKey,
                },
              })
            }
          />
        </details>

        <h4>
          {tFn("testsModule.editor.answerKey", { defaultValue: "Answer key" })}
        </h4>
        <div className="test-editor__options">
          {left.map((l) => (
            <label key={l.id} className="test-editor__full">
              <span>
                {tFn("testsModule.editor.leftCorrectRightId", {
                  defaultValue: "Left {{id}} correct right ID",
                  id: l.id,
                })}
              </span>
              <select
                value={String(answerKey[l.id] ?? "")}
                onChange={(e) =>
                  onChange({
                    ...exercise,
                    config: {
                      ...config,
                      answer_key: {
                        ...answerKey,
                        [l.id]: e.target.value,
                      },
                    },
                  })
                }
              >
                <option value="">—</option>
                {right.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.id} {r.text ? `— ${r.text}` : ""}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        {gradingControls}
      </div>
    );
  }

  if (typeRaw === "image_label") {
    const labels = normalizeIdTextItems(config.labels);
    const hotspots = normalizeHotspots(config.hotspots);
    const answerKey =
      config.answer_key &&
      typeof config.answer_key === "object" &&
      !Array.isArray(config.answer_key)
        ? config.answer_key
        : {};

    const setLabels = (next) =>
      onChange({
        ...exercise,
        config: {
          ...config,
          labels: next.map((x) => ({ id: x.id, text: x.text })),
        },
      });

    const setHotspots = (next) =>
      onChange({
        ...exercise,
        config: {
          ...config,
          hotspots: next.map((x) => ({
            id: x.id,
            x: x.x,
            y: x.y,
            w: x.w,
            h: x.h,
          })),
        },
      });

    return (
      <div className="test-editor__config">
        <label className="test-editor__full">
          <span>
            {tFn("testsModule.editor.imageUrl", { defaultValue: "Image URL" })}
          </span>
          <input
            value={String(config.image_url ?? "")}
            onChange={(e) =>
              onChange({
                ...exercise,
                config: { ...config, image_url: e.target.value },
              })
            }
          />
        </label>

        <h4>{tFn("testsModule.editor.labels", { defaultValue: "Labels" })}</h4>
        <div className="test-editor__options">
          {labels.map((lb, idx) => (
            <div key={lb.id} className="test-editor__optionRow">
              <input
                className="test-editor__optionId"
                value={lb.id}
                onChange={(e) => {
                  const next = labels.slice();
                  next[idx] = { ...next[idx], id: e.target.value };
                  setLabels(next);
                }}
              />
              <input
                className="test-editor__optionText"
                placeholder={tFn("testsModule.editor.labelTextPlaceholder", {
                  defaultValue: "Label text",
                })}
                value={lb.text}
                onChange={(e) => {
                  const next = labels.slice();
                  next[idx] = { ...next[idx], text: e.target.value };
                  setLabels(next);
                }}
              />
              <button
                type="button"
                className="btn ghost"
                onClick={() => setLabels(labels.filter((_, i) => i !== idx))}
                aria-label={tFn("testsModule.editor.removeLabel", {
                  defaultValue: "Remove label",
                })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              setLabels([
                ...labels,
                { id: String(labels.length + 1), text: "" },
              ])
            }
          >
            {tFn("testsModule.editor.addLabel", {
              defaultValue: "+ Add label",
            })}
          </button>
        </div>

        <h4>
          {tFn("testsModule.editor.hotspots", { defaultValue: "Hotspots" })}
        </h4>
        <div className="test-editor__hint">
          {tFn("testsModule.editor.hotspotsHint", {
            defaultValue:
              "Coordinates are percentages relative to the image (x/y = top-left).",
          })}
        </div>

        <ImageHotspotBuilder
          imageUrl={String(config.image_url || "").trim()}
          hotspots={hotspots}
          tFn={tFn}
          onChange={(next) => {
            onChange({
              ...exercise,
              config: {
                ...config,
                hotspots: next.map((x) => ({
                  id: x.id,
                  x: x.x,
                  y: x.y,
                  w: x.w,
                  h: x.h,
                })),
              },
            });
          }}
        />

        <div className="test-editor__options">
          {hotspots.map((hs, idx) => (
            <div
              key={hs.id}
              className="test-editor__optionRow"
              style={{ gridTemplateColumns: "80px 1fr auto" }}
            >
              <input
                className="test-editor__optionId"
                value={hs.id}
                onChange={(e) => {
                  const next = hotspots.slice();
                  next[idx] = { ...next[idx], id: e.target.value };
                  setHotspots(next);
                }}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: "0.5rem",
                }}
              >
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={String(hs.x)}
                  onChange={(e) => {
                    const next = hotspots.slice();
                    next[idx] = { ...next[idx], x: Number(e.target.value) };
                    setHotspots(next);
                  }}
                  placeholder="x"
                />
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={String(hs.y)}
                  onChange={(e) => {
                    const next = hotspots.slice();
                    next[idx] = { ...next[idx], y: Number(e.target.value) };
                    setHotspots(next);
                  }}
                  placeholder="y"
                />
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  value={String(hs.w)}
                  onChange={(e) => {
                    const next = hotspots.slice();
                    next[idx] = { ...next[idx], w: Number(e.target.value) };
                    setHotspots(next);
                  }}
                  placeholder="w"
                />
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  value={String(hs.h)}
                  onChange={(e) => {
                    const next = hotspots.slice();
                    next[idx] = { ...next[idx], h: Number(e.target.value) };
                    setHotspots(next);
                  }}
                  placeholder="h"
                />
              </div>
              <button
                type="button"
                className="btn ghost"
                onClick={() =>
                  setHotspots(hotspots.filter((_, i) => i !== idx))
                }
                aria-label={tFn("testsModule.editor.removeHotspot", {
                  defaultValue: "Remove hotspot",
                })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              setHotspots([
                ...hotspots,
                { id: String(hotspots.length + 1), x: 50, y: 50, w: 18, h: 12 },
              ])
            }
          >
            {tFn("testsModule.editor.addHotspot", {
              defaultValue: "+ Add hotspot",
            })}
          </button>
        </div>

        <h4>
          {tFn("testsModule.editor.answerKey", { defaultValue: "Answer key" })}
        </h4>
        <div className="test-editor__options">
          {hotspots.map((hs) => (
            <label key={hs.id} className="test-editor__full">
              <span>
                {tFn("testsModule.editor.hotspotCorrectLabel", {
                  defaultValue: "Hotspot {{id}} correct label",
                  id: hs.id,
                })}
              </span>
              <select
                value={String(answerKey[hs.id] ?? "")}
                onChange={(e) =>
                  onChange({
                    ...exercise,
                    config: {
                      ...config,
                      answer_key: {
                        ...answerKey,
                        [hs.id]: e.target.value,
                      },
                    },
                  })
                }
              >
                <option value="">—</option>
                {labels.map((lb) => (
                  <option key={lb.id} value={lb.id}>
                    {lb.id} {lb.text ? `— ${lb.text}` : ""}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        {gradingControls}
      </div>
    );
  }

  if (typeRaw === "drag_drop") {
    const mode = String(config.mode || "order");
    const items = normalizeIdTextItems(config.items);
    const categories = normalizeIdTextItems(config.categories);
    const answerKey =
      config.answer_key &&
      typeof config.answer_key === "object" &&
      !Array.isArray(config.answer_key)
        ? config.answer_key
        : {};

    const setItems = (next) =>
      onChange({
        ...exercise,
        config: {
          ...config,
          items: next.map((x) => ({ id: x.id, text: x.text })),
          answer_key: {
            ...answerKey,
            ...(mode === "order"
              ? { ordered_item_ids: next.map((x) => x.id) }
              : null),
          },
        },
      });

    const setCategories = (next) =>
      onChange({
        ...exercise,
        config: {
          ...config,
          categories: next.map((x) => ({ id: x.id, label: x.text })),
        },
      });

    return (
      <div className="test-editor__config">
        <label>
          <span>
            {tFn("testsModule.editor.mode", { defaultValue: "Mode" })}
          </span>
          <select
            value={mode}
            onChange={(e) =>
              onChange({
                ...exercise,
                config: { ...config, mode: e.target.value },
              })
            }
          >
            <option value="order">
              {tFn("testsModule.editor.modeOrder", { defaultValue: "Order" })}
            </option>
            <option value="categorize">
              {tFn("testsModule.editor.modeCategorize", {
                defaultValue: "Categorize",
              })}
            </option>
          </select>
        </label>

        <h4>{tFn("testsModule.editor.items", { defaultValue: "Items" })}</h4>
        <div className="test-editor__options">
          {items.map((it, idx) => (
            <div key={it.id} className="test-editor__optionRow">
              <input
                className="test-editor__optionId"
                value={it.id}
                onChange={(e) => {
                  const next = items.slice();
                  next[idx] = { ...next[idx], id: e.target.value };
                  setItems(next);
                }}
              />
              <input
                className="test-editor__optionText"
                placeholder={tFn("testsModule.editor.textPlaceholder", {
                  defaultValue: "Text",
                })}
                value={it.text}
                onChange={(e) => {
                  const next = items.slice();
                  next[idx] = { ...next[idx], text: e.target.value };
                  setItems(next);
                }}
              />
              <button
                type="button"
                className="btn ghost"
                onClick={() => setItems(items.filter((_, i) => i !== idx))}
                aria-label={tFn("testsModule.editor.removeItem", {
                  defaultValue: "Remove item",
                })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              setItems([...items, { id: String(items.length + 1), text: "" }])
            }
          >
            {tFn("testsModule.editor.addItem", { defaultValue: "+ Add item" })}
          </button>
        </div>

        {mode === "categorize" ? (
          <>
            <h4>
              {tFn("testsModule.editor.categories", {
                defaultValue: "Categories",
              })}
            </h4>
            <div className="test-editor__options">
              {categories.map((c, idx) => (
                <div key={c.id} className="test-editor__optionRow">
                  <input
                    className="test-editor__optionId"
                    value={c.id}
                    onChange={(e) => {
                      const next = categories.slice();
                      next[idx] = { ...next[idx], id: e.target.value };
                      setCategories(next);
                    }}
                  />
                  <input
                    className="test-editor__optionText"
                    placeholder={tFn(
                      "testsModule.editor.categoryLabelPlaceholder",
                      { defaultValue: "Category label" },
                    )}
                    value={c.text}
                    onChange={(e) => {
                      const next = categories.slice();
                      next[idx] = { ...next[idx], text: e.target.value };
                      setCategories(next);
                    }}
                  />
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() =>
                      setCategories(categories.filter((_, i) => i !== idx))
                    }
                    aria-label={tFn("testsModule.editor.removeCategory", {
                      defaultValue: "Remove category",
                    })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn ghost"
                onClick={() =>
                  setCategories([
                    ...categories,
                    { id: String(categories.length + 1), text: "" },
                  ])
                }
              >
                {tFn("testsModule.editor.addCategory", {
                  defaultValue: "+ Add category",
                })}
              </button>
            </div>

            <h4>
              {tFn("testsModule.editor.answerKey", {
                defaultValue: "Answer key",
              })}
            </h4>
            <div className="test-editor__options">
              {items.map((it) => (
                <label key={it.id} className="test-editor__full">
                  <span>
                    {tFn("testsModule.editor.itemCorrectCategory", {
                      defaultValue: "Item {{id}} correct category",
                      id: it.id,
                    })}
                  </span>
                  <select
                    value={String(
                      (answerKey.category_by_item_id || {})[it.id] ?? "",
                    )}
                    onChange={(e) =>
                      onChange({
                        ...exercise,
                        config: {
                          ...config,
                          answer_key: {
                            ...answerKey,
                            category_by_item_id: {
                              ...(answerKey.category_by_item_id || {}),
                              [it.id]: e.target.value,
                            },
                          },
                        },
                      })
                    }
                  >
                    <option value="">—</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.id} {c.text ? `— ${c.text}` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </>
        ) : (
          <>
            <h4>
              {tFn("testsModule.editor.answerKey", {
                defaultValue: "Answer key",
              })}
            </h4>
            <div className="test-editor__hint">
              {tFn("testsModule.editor.orderAnswerKeyHint", {
                defaultValue: "Order answer key is the current items order.",
              })}
            </div>
          </>
        )}

        {gradingControls}
      </div>
    );
  }

  if (type === "select_words") {
    const mode = String(config.mode || "circle");
    const items = normalizeWordItems(config.items);

    const updateItems = (next) => {
      onChange({
        ...exercise,
        config: {
          ...config,
          items: next,
        },
      });
    };

    const answerKey = Array.isArray(config.answer_key)
      ? config.answer_key.map(String)
      : [];

    return (
      <div className="test-editor__config">
        <label>
          <span>
            {tFn("testsModule.editor.mode", { defaultValue: "Mode" })}
          </span>
          <select
            value={mode}
            onChange={(e) =>
              onChange({
                ...exercise,
                config: { ...config, mode: e.target.value },
              })
            }
          >
            <option value="circle">
              {tFn("testsModule.editor.selectWordsCircle", {
                defaultValue: "Circle (Entoure)",
              })}
            </option>
            <option value="highlight">
              {tFn("testsModule.editor.selectWordsHighlight", {
                defaultValue: "Highlight (Colorie)",
              })}
            </option>
          </select>
        </label>

        <div className="test-editor__options">
          {items.map((it, idx) => (
            <div key={it.id} className="test-editor__optionRow">
              <input
                className="test-editor__optionId"
                value={it.id}
                onChange={(e) => {
                  const next = items.slice();
                  next[idx] = { ...next[idx], id: e.target.value };
                  updateItems(next);
                }}
              />
              <input
                className="test-editor__optionText"
                placeholder={tFn("testsModule.editor.wordPlaceholder", {
                  defaultValue: "Word",
                })}
                value={it.text}
                onChange={(e) => {
                  const next = items.slice();
                  next[idx] = { ...next[idx], text: e.target.value };
                  updateItems(next);
                }}
              />
              <button
                type="button"
                className="btn ghost"
                onClick={() => updateItems(items.filter((_, i) => i !== idx))}
                aria-label={tFn("testsModule.editor.removeWord", {
                  defaultValue: "Remove word",
                })}
              >
                ×
              </button>
            </div>
          ))}

          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              updateItems([
                ...items,
                { id: String(items.length + 1), text: "" },
              ])
            }
          >
            {tFn("testsModule.editor.addWord", { defaultValue: "+ Add word" })}
          </button>
        </div>

        <p className="test-editor__hint">
          {tFn("testsModule.editor.selectWordsHint", {
            defaultValue:
              "Use this for exercises like: “Entoure les mois …” or “Colorie les jours …”.",
          })}
        </p>

        <h4>
          {tFn("testsModule.editor.answerKey", { defaultValue: "Answer key" })}
        </h4>
        <div className="test-editor__options">
          {items.map((it) => {
            const on = answerKey.includes(String(it.id));
            return (
              <label key={it.id} className="test-editor__inline">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = on
                      ? answerKey.filter((x) => x !== String(it.id))
                      : [...answerKey, String(it.id)];
                    onChange({
                      ...exercise,
                      config: { ...config, answer_key: next },
                    });
                  }}
                />
                <span>
                  {tFn("testsModule.editor.correctId", {
                    defaultValue: "Correct: {{id}}",
                    id: it.id,
                  })}
                </span>
              </label>
            );
          })}
        </div>

        {gradingControls}
      </div>
    );
  }

  if (type === "short_text") {
    const accepted = Array.isArray(config.answer_key)
      ? config.answer_key.map(String)
      : [];
    // Always show at least one box to type the expected answer.
    const boxes = accepted.length ? accepted : [""];
    const writeAccepted = (list) =>
      onChange({ ...exercise, config: { ...config, answer_key: list } });

    const placeholderField = (
      <label>
        <span>
          {tFn("testsModule.editor.placeholderLabel", {
            defaultValue: "Placeholder",
          })}
        </span>
        <input
          value={String(config.placeholder ?? "")}
          onChange={(e) =>
            onChange({
              ...exercise,
              config: { ...config, placeholder: e.target.value },
            })
          }
        />
      </label>
    );

    return (
      <div className="test-editor__config">
        <p className="test-editor__hint">
          {tFn("testsModule.editor.acceptedHint", {
            defaultValue:
              "Write the expected answer. Add other spellings you also accept.",
          })}
        </p>
        <div className="test-editor__choices">
          {boxes.map((value, index) => (
            <div key={index} className="test-editor__choice">
              <i
                className="fa-solid fa-check test-editor__acceptedIcon"
                aria-hidden="true"
              />
              <input
                className="test-editor__choiceText"
                value={value}
                placeholder={tFn("testsModule.editor.acceptedPlaceholder", {
                  defaultValue: "Accepted answer",
                })}
                onChange={(e) =>
                  writeAccepted(
                    boxes.map((v, i) => (i === index ? e.target.value : v)),
                  )
                }
              />
              <button
                type="button"
                className="test-editor__iconBtn"
                onClick={() => writeAccepted(boxes.filter((_, i) => i !== index))}
                disabled={boxes.length <= 1}
                aria-label={tFn("testsModule.editor.removeAnswer", {
                  defaultValue: "Remove answer",
                })}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
        <div className="test-editor__rowActions">
          <button
            type="button"
            className="btn ghost"
            onClick={() => writeAccepted([...boxes, ""])}
          >
            {tFn("testsModule.editor.addAnswer", {
              defaultValue: "+ Add another accepted answer",
            })}
          </button>
        </div>
        {renderAdvanced(placeholderField)}
      </div>
    );
  }

  if (type === "long_text") {
    return (
      <div className="test-editor__config">
        <label>
          <span>
            {tFn("testsModule.editor.rows", { defaultValue: "Rows" })}
          </span>
          <input
            type="number"
            min="2"
            value={String(config.rows ?? 6)}
            onChange={(e) => {
              const num = Number(e.target.value);
              onChange({
                ...exercise,
                config: { ...config, rows: Number.isNaN(num) ? 6 : num },
              });
            }}
          />

          <div className="test-editor__hint">
            {tFn("testsModule.editor.longTextManualHint", {
              defaultValue: "Long text is manual grading by default.",
            })}
          </div>

          {gradingControls}
        </label>
      </div>
    );
  }

  if (typeRaw === "essay") {
    return (
      <div className="test-editor__config">
        <label>
          <span>
            {tFn("testsModule.editor.rows", { defaultValue: "Rows" })}
          </span>
          <input
            type="number"
            min="2"
            value={String(config.rows ?? 10)}
            onChange={(e) => {
              const num = Number(e.target.value);
              onChange({
                ...exercise,
                config: { ...config, rows: Number.isNaN(num) ? 10 : num },
              });
            }}
          />
        </label>
        <div className="test-editor__hint">
          {tFn("testsModule.editor.essayManualHint", {
            defaultValue: "Essay is manual grading by default.",
          })}
        </div>
        {gradingControls}
      </div>
    );
  }

  if (type === "diagram_label") {
    const items = normalizeIdTextItems(config.items);
    const answerKey =
      config.answer_key &&
      typeof config.answer_key === "object" &&
      !Array.isArray(config.answer_key)
        ? config.answer_key
        : {};
    return (
      <div className="test-editor__config">
        <label className="test-editor__full">
          <span>
            {tFn("testsModule.editor.imageUrlOptional", {
              defaultValue: "Image URL (optional)",
            })}
          </span>
          <input
            value={String(config.image_url ?? "")}
            onChange={(e) =>
              onChange({
                ...exercise,
                config: { ...config, image_url: e.target.value },
              })
            }
          />
        </label>

        <div className="test-editor__options">
          {items.map((it, idx) => (
            <div key={it.id} className="test-editor__optionRow">
              <input
                className="test-editor__optionId"
                value={it.id}
                onChange={(e) => {
                  const next = items.slice();
                  next[idx] = { ...next[idx], id: e.target.value };
                  onChange({
                    ...exercise,
                    config: {
                      ...config,
                      items: next.map((x) => ({ id: x.id, label: x.text })),
                    },
                  });
                }}
              />
              <input
                className="test-editor__optionText"
                placeholder={tFn("testsModule.editor.labelPlaceholder", {
                  defaultValue: "Label",
                })}
                value={it.text}
                onChange={(e) => {
                  const next = items.slice();
                  next[idx] = { ...next[idx], text: e.target.value };
                  onChange({
                    ...exercise,
                    config: {
                      ...config,
                      items: next.map((x) => ({ id: x.id, label: x.text })),
                    },
                  });
                }}
              />
              <button
                type="button"
                className="btn ghost"
                onClick={() =>
                  onChange({
                    ...exercise,
                    config: {
                      ...config,
                      items: items
                        .filter((_, i) => i !== idx)
                        .map((x) => ({ id: x.id, label: x.text })),
                    },
                  })
                }
                aria-label={tFn("testsModule.editor.removeItem", {
                  defaultValue: "Remove item",
                })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              onChange({
                ...exercise,
                config: {
                  ...config,
                  items: [
                    ...items,
                    { id: String(items.length + 1), text: "" },
                  ].map((x) => ({ id: x.id, label: x.text })),
                },
              })
            }
          >
            {tFn("testsModule.editor.addItem", { defaultValue: "+ Add item" })}
          </button>
        </div>

        <h4>
          {tFn("testsModule.editor.answerKey", { defaultValue: "Answer key" })}
        </h4>
        <div className="test-editor__options">
          {items.map((it) => {
            const current = Array.isArray(answerKey[it.id])
              ? answerKey[it.id].join(", ")
              : "";
            return (
              <label key={it.id} className="test-editor__full">
                <span>
                  {tFn("testsModule.editor.itemAcceptedAnswersComma", {
                    defaultValue:
                      "Item {{id}} accepted answers (comma-separated)",
                    id: it.id,
                  })}
                </span>
                <input
                  value={current}
                  onChange={(e) => {
                    const list = String(e.target.value || "")
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean);
                    onChange({
                      ...exercise,
                      config: {
                        ...config,
                        answer_key: {
                          ...answerKey,
                          [it.id]: list,
                        },
                      },
                    });
                  }}
                />
              </label>
            );
          })}
        </div>

        {gradingControls}
      </div>
    );
  }

  return (
    <div className="test-editor__config">
      <div className="test-editor__hint">
        {tFn("testsModule.editor.noVisualEditor", {
          defaultValue:
            "This exercise type doesn't have a visual editor yet. Change the exercise type (above) to continue editing.",
        })}
      </div>
      {gradingControls}
    </div>
  );
}

export default function ProgramTestEditor({
  overrideTestId,
  embedded = false,
} = {}) {
  const { testId: routeTestId } = useParams();
  const testId = overrideTestId ?? routeTestId;
  const { accessToken, effectiveRole } = useAuth();
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const tFn = useCallback(
    (key, options) => {
      try {
        return t(key, options);
      } catch {
        return options?.defaultValue || key;
      }
    },
    [t],
  );

  const isTeacherOrAdmin =
    effectiveRole === "teacher" ||
    ["admin", "superadmin"].includes(effectiveRole);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [test, setTest] = useState(null);
  const [testForm, setTestForm] = useState({
    title: "",
    description: "",
    level_grade: "",
    duration_minutes: "",
    test_type: "mixed_test",
    is_published: false,
  });
  const [savingTest, setSavingTest] = useState(false);
  const latestTestFormEarly = useRef(null);

  const [draftExercises, setDraftExercises] = useState([]);
  const [exerciseBusyId, setExerciseBusyId] = useState(null);

  // Autosave: questions and test details are saved shortly after each edit.
  const [saveStatus, setSaveStatus] = useState({}); // key -> pending|saving|saved|error
  const saveTimers = useRef(new Map());
  const latestDrafts = useRef(new Map());
  const [publishProblems, setPublishProblems] = useState(null);
  const [showMoreTypes, setShowMoreTypes] = useState(false);

  const [newExercise, setNewExercise] = useState({
    exercise_type: "multiple_choice",
    prompt: "",
    points: 1,
    qcmMode: "single",
  });
  const [addingExercise, setAddingExercise] = useState(false);

  // Program names, so the header shows "Coran" instead of the slug.
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    let ignore = false;
    programsService
      .fetchPrograms({ accessToken })
      .then((payload) => {
        if (!ignore) {
          setPrograms(
            Array.isArray(payload?.programs) ? payload.programs : [],
          );
        }
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, [accessToken]);
  const programName = test?.program_slug
    ? programs.find((p) => p.slug === test.program_slug)?.title ||
      test.program_slug
    : "";

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const payload = await programTestsService.fetchProgramTest({
          accessToken,
          testId,
        });
        if (ignore) return;
        setTest(payload);
        const loadedForm = {
          title: payload.title || "",
          description: payload.description || "",
          level_grade: payload.level_grade || "",
          duration_minutes: payload.duration_minutes ?? "",
          test_type: normalizeTestType(payload.test_type),
          is_published: Boolean(payload.is_published),
        };
        setTestForm(loadedForm);
        latestTestFormEarly.current = loadedForm;
        const loadedExercises = Array.isArray(payload.exercises)
          ? payload.exercises
          : [];
        latestDrafts.current = new Map(loadedExercises.map((e) => [e.id, e]));
        setDraftExercises(loadedExercises);
      } catch (e) {
        if (ignore) return;
        setError(
          e?.message ||
            tFn("testsModule.take.failedLoad", {
              defaultValue: "Failed to load test",
            }),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    if (testId && accessToken) {
      load();
    } else {
      setLoading(false);
    }

    return () => {
      ignore = true;
    };
  }, [accessToken, tFn, testId]);

  const canEdit = isTeacherOrAdmin && Boolean(accessToken);

  const sortedExercises = useMemo(() => {
    const list = Array.isArray(draftExercises) ? draftExercises : [];
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [draftExercises]);

  const effectiveTestType = normalizeTestType(
    testForm.test_type || test?.test_type,
  );
  const allowedExerciseTypes = useMemo(
    () => allowedExerciseTypesForTestType(effectiveTestType),
    [effectiveTestType],
  );
  const allowedExerciseTypesSet = useMemo(
    () => new Set(allowedExerciseTypes),
    [allowedExerciseTypes],
  );
  const hasStimulus = useMemo(
    () =>
      sortedExercises.some(
        (e) => String(e?.exercise_type || "") === "stimulus",
      ),
    [sortedExercises],
  );

  useEffect(() => {
    // Keep new exercise type aligned with the selected test type.
    setNewExercise((prev) => {
      let nextType =
        String(prev.exercise_type || "").trim() || "multiple_choice";

      // Only auto-adjust if the currently selected type isn't allowed.
      if (!allowedExerciseTypesSet.has(nextType)) {
        if (effectiveTestType === "text_comprehension" && !hasStimulus)
          nextType = "stimulus";
        else if (effectiveTestType === "written_expression")
          nextType = "long_text";
        else if (effectiveTestType === "qcm") nextType = "multiple_choice";
        else nextType = allowedExerciseTypes[0] || "multiple_choice";
      }

      if (nextType === prev.exercise_type) return prev;
      return { ...prev, exercise_type: nextType };
    });
  }, [
    allowedExerciseTypes,
    allowedExerciseTypesSet,
    effectiveTestType,
    hasStimulus,
  ]);

  const saveTest = async () => {
    if (!canEdit) return;

    setSavingTest(true);
    setError("");
    try {
      const form = latestTestForm.current;
      const payload = {
        title: form.title,
        description: form.description,
        level_grade: form.level_grade,
        duration_minutes:
          form.duration_minutes === "" ? null : form.duration_minutes,
        test_type: normalizeTestType(form.test_type),
      };
      const updated = await programTestsService.updateProgramTest({
        accessToken,
        testId,
        payload,
      });
      setTest(updated);
      setSaveStatus((prev) => ({ ...prev, details: "saved" }));
    } catch (e) {
      setSaveStatus((prev) => ({ ...prev, details: "error" }));
      setError(
        e?.message ||
          tFn("testsModule.editor.failedSaveTest", {
            defaultValue: "Failed to save test",
          }),
      );
    } finally {
      setSavingTest(false);
    }
  };

  const latestTestForm = latestTestFormEarly;
  if (latestTestForm.current === null) latestTestForm.current = testForm;
  const updateTestForm = (patch) => {
    setTestForm((prev) => {
      const next = { ...prev, ...patch };
      latestTestForm.current = next;
      return next;
    });
    scheduleSave("details", () => saveTest(), 900);
  };

  const persistExercise = useCallback(
    async (exerciseId) => {
      const exercise = latestDrafts.current.get(exerciseId);
      if (!exercise || !accessToken) return;
      const key = `ex-${exerciseId}`;
      setSaveStatus((prev) => ({ ...prev, [key]: "saving" }));
      try {
        await programTestsService.updateProgramTestExercise({
          accessToken,
          exerciseId,
          payload: {
            order: exercise.order,
            points: exercise.points,
            prompt: exercise.prompt,
            config: exercise.config,
            exercise_type: exercise.exercise_type,
          },
        });
        // Only mark saved if nothing changed while the request was running.
        if (latestDrafts.current.get(exerciseId) === exercise) {
          setSaveStatus((prev) => ({ ...prev, [key]: "saved" }));
        }
      } catch (e) {
        setSaveStatus((prev) => ({ ...prev, [key]: "error" }));
        setError(
          e?.message ||
            tFn("testsModule.editor.failedSaveExercise", {
              defaultValue: "Failed to save exercise",
            }),
        );
      }
    },
    [accessToken, tFn],
  );

  const scheduleSave = useCallback((key, run, delay = 700) => {
    const timers = saveTimers.current;
    if (timers.has(key)) clearTimeout(timers.get(key));
    setSaveStatus((prev) => ({ ...prev, [key]: "pending" }));
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key);
        run();
      }, delay),
    );
  }, []);

  // Every edit to a question goes through here.
  const updateExercise = useCallback(
    (next, { immediate = false } = {}) => {
      latestDrafts.current.set(next.id, next);
      setDraftExercises((prev) => prev.map((e) => (e.id === next.id ? next : e)));
      scheduleSave(`ex-${next.id}`, () => persistExercise(next.id), immediate ? 0 : 700);
    },
    [persistExercise, scheduleSave],
  );

  // Save anything still waiting when the editor closes.
  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      timers.forEach((timer, key) => {
        clearTimeout(timer);
        if (key.startsWith("ex-")) persistExercise(Number(key.slice(3)));
      });
      timers.clear();
    };
  }, [persistExercise]);

  const hasUnsaved = Object.values(saveStatus).some(
    (v) => v === "pending" || v === "saving",
  );
  const hasSaveError = Object.values(saveStatus).includes("error");

  useEffect(() => {
    if (!hasUnsaved) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsaved]);

  const moveExercise = (exerciseId, delta) => {
    const list = sortedExercises.slice();
    const index = list.findIndex((e) => e.id === exerciseId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    list.forEach((exercise, order) => {
      if (exercise.order !== order) updateExercise({ ...exercise, order }, { immediate: true });
    });
  };

  // Publishing: list what still needs fixing first.
  const problemList = useMemo(() => {
    const items = [];
    if (!sortedExercises.length) items.push({ key: "noQuestions" });
    let number = 0;
    sortedExercises.forEach((exercise) => {
      const isStimulus = exercise.exercise_type === "stimulus";
      if (!isStimulus) number += 1;
      exerciseProblems(exercise).forEach((key) =>
        items.push({ key, exerciseId: exercise.id, number: isStimulus ? null : number }),
      );
    });
    return items;
  }, [sortedExercises]);

  const setPublished = async (published, { force = false } = {}) => {
    if (!canEdit) return;
    if (published && problemList.length && !force) {
      setPublishProblems(problemList);
      return;
    }
    setPublishProblems(null);
    setSavingTest(true);
    setError("");
    try {
      const updated = await programTestsService.updateProgramTest({
        accessToken,
        testId,
        payload: { is_published: published },
      });
      setTest(updated);
      setTestForm((prev) => ({ ...prev, is_published: Boolean(updated?.is_published ?? published) }));
    } catch (e) {
      setError(
        e?.message ||
          tFn("testsModule.editor.failedSaveTest", {
            defaultValue: "Failed to save test",
          }),
      );
    } finally {
      setSavingTest(false);
    }
  };

  const deleteExercise = async (exerciseId) => {
    if (!canEdit) return;
    if (
      !window.confirm(
        tFn("testsModule.editor.confirmDeleteExercise", {
          defaultValue: "Delete this exercise?",
        }),
      )
    )
      return;

    setExerciseBusyId(exerciseId);
    setError("");
    try {
      await programTestsService.deleteProgramTestExercise({
        accessToken,
        exerciseId,
      });
      setDraftExercises((prev) => prev.filter((e) => e.id !== exerciseId));
    } catch (e) {
      setError(
        e?.message ||
          tFn("testsModule.editor.failedDeleteExercise", {
            defaultValue: "Failed to delete exercise",
          }),
      );
    } finally {
      setExerciseBusyId(null);
    }
  };

  const addExercise = async (event) => {
    event.preventDefault();
    if (!canEdit) return;

    setAddingExercise(true);
    setError("");
    try {
      let exerciseType =
        String(newExercise.exercise_type || "").trim() || "multiple_choice";
      if (!allowedExerciseTypesSet.has(exerciseType))
        exerciseType = allowedExerciseTypes[0] || "multiple_choice";

      let config = buildDefaultConfig(exerciseType);
      if (exerciseType === "multiple_choice" && effectiveTestType === "qcm") {
        config = {
          ...config,
          allow_multiple:
            String(newExercise.qcmMode || "single") === "multiple",
        };
      }
      if (
        (exerciseType === "long_text" ||
          exerciseType === "task" ||
          exerciseType === "essay") &&
        effectiveTestType === "written_expression"
      ) {
        config = { ...config, rows: 14 };
      }

      const points = exerciseType === "stimulus" ? 1 : newExercise.points;

      const created = await programTestsService.createProgramTestExercise({
        accessToken,
        testId,
        payload: {
          exercise_type: exerciseType,
          prompt: newExercise.prompt,
          points,
          config,
          order: sortedExercises.length,
        },
      });

      latestDrafts.current.set(created.id, created);
      setDraftExercises((prev) => [...prev, created]);
      setSaveStatus((prev) => ({ ...prev, [`ex-${created.id}`]: "saved" }));
      setTimeout(() => {
        document
          .getElementById(`exercise-${created.id}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      setNewExercise((prev) => {
        return {
          ...prev,
          exercise_type: prev.exercise_type,
          prompt: "",
          points: 1,
        };
      });
    } catch (e) {
      setError(
        e?.message ||
          tFn("testsModule.editor.failedAddExercise", {
            defaultValue: "Failed to add exercise",
          }),
      );
    } finally {
      setAddingExercise(false);
    }
  };

  const Wrapper = embedded ? "section" : "main";
  const containerClassName = embedded
    ? "test-editor test-editor--embedded"
    : "test-editor";

  if (!isTeacherOrAdmin) {
    return (
      <Wrapper className={containerClassName} dir={dir}>
        <div className="container test-editor__container">
          <div className="test-editor__error">
            {tFn("testsModule.common.notAllowed", {
              defaultValue: "Not allowed",
            })}
          </div>
          <Link className="btn ghost" to="/tests">
            {tFn("testsModule.common.back", { defaultValue: "Back" })}
          </Link>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper className={containerClassName} dir={dir}>
      <div className="container test-editor__container">
        <header className="test-editor__header">
          <div>
            <h1 className="test-editor__title">
              {tFn("testsModule.editor.editTestTitle", {
                defaultValue: "Edit test",
              })}
            </h1>
            <p className="test-editor__subtitle">
              {tFn("testsModule.submissions.testNumber", {
                id: testId,
                defaultValue: `Test #${testId}`,
              })}{" "}
              {programName ? `— ${programName}` : ""}
            </p>
          </div>
          <span
            className={`test-editor__saveState${
              hasSaveError ? " is-error" : hasUnsaved ? " is-saving" : ""
            }`}
            role="status"
          >
            <i
              className={`fa-solid ${
                hasSaveError
                  ? "fa-triangle-exclamation"
                  : hasUnsaved
                    ? "fa-rotate"
                    : "fa-cloud"
              }`}
              aria-hidden="true"
            />
            {hasSaveError
              ? tFn("testsModule.editor.saveError", {
                  defaultValue: "Some changes were not saved",
                })
              : hasUnsaved
                ? tFn("testsModule.common.saving", { defaultValue: "Saving..." })
                : tFn("testsModule.editor.allSaved", {
                    defaultValue: "All changes saved",
                  })}
          </span>
          <div className="test-editor__headerActions">
            <Link className="btn ghost" to="/tests">
              {tFn("testsModule.common.back", { defaultValue: "Back" })}
            </Link>
            <Link className="btn ghost" to={`/tests/${testId}/submissions`}>
              {tFn("testsModule.submissions.title", {
                defaultValue: "Submissions",
              })}
            </Link>
          </div>
        </header>

        {error ? <div className="test-editor__error">{error}</div> : null}

        {loading ? (
          <div className="test-editor__loading">
            {tFn("testsModule.common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : null}

        {!loading && test ? (
          <section className="test-editor__card">
            <h2 className="test-editor__sectionTitle">
              {tFn("testsModule.editor.details", { defaultValue: "Details" })}
            </h2>
            <div className="test-editor__grid">
              <label className="test-editor__full">
                <span>
                  {tFn("testsModule.common.title", { defaultValue: "Title" })}
                </span>
                <input
                  value={testForm.title}
                  onChange={(e) =>
                    updateTestForm({ title: e.target.value })
                  }
                />
              </label>

              <label className="test-editor__full">
                <span>
                  {tFn("testsModule.common.description", {
                    defaultValue: "Description",
                  })}
                </span>
                <textarea
                  rows={3}
                  value={testForm.description}
                  onChange={(e) =>
                    updateTestForm({ description: e.target.value })
                  }
                />
              </label>

              <label>
                <span>
                  {tFn("testsModule.common.level", { defaultValue: "Level" })}
                </span>
                <input
                  value={testForm.level_grade}
                  onChange={(e) =>
                    updateTestForm({ level_grade: e.target.value })
                  }
                />
              </label>

              <label>
                <span>
                  {tFn("testsModule.editor.durationMinutes", {
                    defaultValue: "Duration (minutes)",
                  })}
                </span>
                <input
                  type="number"
                  min="1"
                  value={String(testForm.duration_minutes ?? "")}
                  onChange={(e) =>
                    updateTestForm({ duration_minutes: e.target.value })
                  }
                />
              </label>

              <label>
                <span>
                  {tFn("testsModule.editor.testType", {
                    defaultValue: "Test type",
                  })}
                </span>
                <select
                  value={normalizeTestType(testForm.test_type)}
                  onChange={(e) => updateTestForm({ test_type: e.target.value })}
                >
                  {TEST_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {tFn(`testsModule.testTypes.${opt.value}`, {
                        defaultValue: opt.label,
                      })}
                    </option>
                  ))}
                </select>
              </label>

              <div className="test-editor__publish">
                <span
                  className={`test-editor__status ${
                    testForm.is_published ? "is-published" : "is-draft"
                  }`}
                >
                  <i
                    className={`fa-solid ${
                      testForm.is_published ? "fa-circle-check" : "fa-pen-ruler"
                    }`}
                    aria-hidden="true"
                  />
                  {testForm.is_published
                    ? tFn("testsModule.editor.statusPublished", {
                        defaultValue: "Published — students can take it",
                      })
                    : tFn("testsModule.editor.statusDraft", {
                        defaultValue: "Draft — students can't see it yet",
                      })}
                </span>
                <div className="test-editor__actions">
                  <Link className="btn ghost" to={`/tests/${testId}`}>
                    {tFn("testsModule.common.preview", {
                      defaultValue: "Preview",
                    })}
                  </Link>
                  <button
                    className={`btn ${testForm.is_published ? "ghost" : "primary"}`}
                    type="button"
                    onClick={() => setPublished(!testForm.is_published)}
                    disabled={savingTest}
                  >
                    {testForm.is_published
                      ? tFn("testsModule.editor.unpublish", {
                          defaultValue: "Unpublish",
                        })
                      : tFn("testsModule.editor.publish", {
                          defaultValue: "Publish",
                        })}
                  </button>
                </div>
              </div>

              {publishProblems ? (
                <div className="test-editor__problems" role="alert">
                  <strong>
                    {tFn("testsModule.editor.problemsTitle", {
                      defaultValue: "Fix these before publishing:",
                    })}
                  </strong>
                  <ul>
                    {publishProblems.map((item, index) => (
                      <li key={`${item.key}-${item.exerciseId || index}`}>
                        {item.exerciseId ? (
                          <button
                            type="button"
                            className="test-editor__problemLink"
                            onClick={() =>
                              document
                                .getElementById(`exercise-${item.exerciseId}`)
                                ?.scrollIntoView({ behavior: "smooth", block: "center" })
                            }
                          >
                            {item.number
                              ? tFn("testsModule.editor.questionN", {
                                  defaultValue: "Question {{n}}",
                                  n: item.number,
                                })
                              : tFn("testsModule.exerciseTypes.stimulus", {
                                  defaultValue: "Text",
                                })}
                          </button>
                        ) : null}
                        {item.exerciseId ? " — " : ""}
                        {tFn(`testsModule.editor.problems.${item.key}`, {
                          defaultValue: item.key,
                        })}
                      </li>
                    ))}
                  </ul>
                  <div className="test-editor__actions">
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => setPublishProblems(null)}
                    >
                      {tFn("testsModule.editor.keepEditing", {
                        defaultValue: "Keep editing",
                      })}
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => setPublished(true, { force: true })}
                    >
                      {tFn("testsModule.editor.publishAnyway", {
                        defaultValue: "Publish anyway",
                      })}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="test-editor__card test-editor__card--exercises">
          <h2 className="test-editor__sectionTitle">
            {tFn("testsModule.editor.questionsTitle", {
              defaultValue: "Questions",
            })}
          </h2>

          {effectiveTestType === "text_comprehension" && !hasStimulus ? (
            <div className="test-editor__note">
              {tFn("testsModule.editor.comprehensionHint", {
                defaultValue:
                  "Step 1: Add the reading text (the first item). Then you can add questions.",
              })}
            </div>
          ) : null}

          {effectiveTestType === "written_expression" ? (
            <div className="test-editor__note">
              {tFn("testsModule.editor.writtenHint", {
                defaultValue:
                  "Written Expression: add the topic; the student will see it and write in a large text box.",
              })}
            </div>
          ) : null}

          <form className="test-editor__add" onSubmit={addExercise}>
            <div className="test-editor__full">
              <span className="test-editor__pickerLabel">
                {tFn("testsModule.editor.chooseType", {
                  defaultValue: "1. Choose the type of question",
                })}
              </span>
              <div className="test-editor__typePicker" role="radiogroup">
                {allowedExerciseTypes
                  .filter((value) => showMoreTypes || COMMON_TYPE_VALUES.has(value))
                  .map((value) => {
                    const icon =
                      COMMON_EXERCISE_TYPES.find((x) => x.value === value)?.icon ||
                      EXERCISE_TYPE_ICONS[value] ||
                      "fa-circle-question";
                    const active = newExercise.exercise_type === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`test-editor__typeCard${active ? " is-active" : ""}`}
                        onClick={() =>
                          setNewExercise((p) => ({ ...p, exercise_type: value }))
                        }
                        disabled={addingExercise}
                      >
                        <i className={`fa-solid ${icon}`} aria-hidden="true" />
                        <strong>
                          {tFn(`testsModule.editor.typeNames.${value}`, {
                            defaultValue: tFn(`testsModule.exerciseTypes.${value}`, {
                              defaultValue: value,
                            }),
                          })}
                        </strong>
                        <small>
                          {tFn(`testsModule.editor.typeHints.${value}`, {
                            defaultValue: "",
                          })}
                        </small>
                      </button>
                    );
                  })}
              </div>
              {allowedExerciseTypes.some((v) => !COMMON_TYPE_VALUES.has(v)) ? (
                <button
                  type="button"
                  className="test-editor__moreTypes"
                  onClick={() => setShowMoreTypes((v) => !v)}
                >
                  {showMoreTypes
                    ? tFn("testsModule.editor.fewerTypes", {
                        defaultValue: "Fewer types",
                      })
                    : tFn("testsModule.editor.moreTypes", {
                        defaultValue: "More types",
                      })}
                </button>
              ) : null}
            </div>

            {effectiveTestType === "qcm" &&
            String(newExercise.exercise_type || "") === "multiple_choice" ? (
              <label>
                <span>
                  {tFn("testsModule.editor.qcmMode", {
                    defaultValue: "QCM mode",
                  })}
                </span>
                <select
                  value={String(newExercise.qcmMode || "single")}
                  onChange={(e) =>
                    setNewExercise((p) => ({ ...p, qcmMode: e.target.value }))
                  }
                  disabled={addingExercise}
                >
                  <option value="single">
                    {tFn("testsModule.editor.qcmModeSingle", {
                      defaultValue: "Single choice",
                    })}
                  </option>
                  <option value="multiple">
                    {tFn("testsModule.editor.qcmModeMultiple", {
                      defaultValue: "Multiple choice",
                    })}
                  </option>
                </select>
              </label>
            ) : null}

            <label className="test-editor__full">
              <span>
                {effectiveTestType === "text_comprehension" && !hasStimulus
                  ? tFn("testsModule.editor.text", { defaultValue: "Text" })
                  : effectiveTestType === "written_expression"
                    ? tFn("testsModule.editor.topic", { defaultValue: "Topic" })
                    : String(newExercise.exercise_type) === "stimulus"
                      ? tFn("testsModule.editor.stimulusPromptLabel", {
                          defaultValue: "2. Paste the text students will read",
                        })
                      : tFn("testsModule.editor.questionPromptLabel", {
                          defaultValue: "2. Write the question",
                        })}
              </span>
              <textarea
                rows={
                  (effectiveTestType === "text_comprehension" && !hasStimulus) ||
                  String(newExercise.exercise_type) === "stimulus"
                    ? 8
                    : 3
                }
                value={newExercise.prompt}
                onChange={(e) =>
                  setNewExercise((p) => ({ ...p, prompt: e.target.value }))
                }
                disabled={addingExercise}
                required
              />
            </label>

            <div className="test-editor__actions">
              <button
                className="btn primary"
                type="submit"
                disabled={addingExercise}
              >
                {addingExercise
                  ? tFn("testsModule.editor.adding", {
                      defaultValue: "Adding...",
                    })
                  : tFn("testsModule.editor.addQuestion", {
                      defaultValue: "Add the question",
                    })}
              </button>
            </div>
          </form>

          <div className="test-editor__exerciseList">
            {sortedExercises.map((exercise, index) => {
              const isStimulus = exercise.exercise_type === "stimulus";
              const number = sortedExercises
                .slice(0, index + 1)
                .filter((e) => e.exercise_type !== "stimulus").length;
              const problems = exerciseProblems(exercise);
              const status = saveStatus[`ex-${exercise.id}`];
              return (
                <article
                  className={`test-editor__exercise${
                    isStimulus ? " test-editor__exercise--stimulus" : ""
                  }`}
                  key={exercise.id}
                  id={`exercise-${exercise.id}`}
                >
                  <header className="test-editor__exerciseHead">
                    <div className="test-editor__exerciseHeadTitle">
                      <strong>
                        {isStimulus
                          ? tFn("testsModule.editor.typeNames.stimulus", {
                              defaultValue: "Text to read",
                            })
                          : tFn("testsModule.editor.questionN", {
                              defaultValue: "Question {{n}}",
                              n: number,
                            })}
                      </strong>
                      <span className="test-editor__pill">
                        {tFn(`testsModule.editor.typeNames.${exercise.exercise_type}`, {
                          defaultValue: tFn(
                            `testsModule.exerciseTypes.${exercise.exercise_type}`,
                            { defaultValue: String(exercise.exercise_type || "") },
                          ),
                        })}
                      </span>
                      {status === "error" ? (
                        <span className="test-editor__pill test-editor__pill--error">
                          {tFn("testsModule.editor.notSaved", {
                            defaultValue: "Not saved",
                          })}
                        </span>
                      ) : null}
                    </div>
                    <div className="test-editor__exerciseHeadActions">
                      {!isStimulus ? (
                        <label className="test-editor__points">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={String(exercise.points ?? 1)}
                            onChange={(e) => {
                              const num = parseInt(e.target.value, 10);
                              updateExercise({
                                ...exercise,
                                points: Number.isNaN(num) ? 1 : Math.max(1, num),
                              });
                            }}
                            aria-label={tFn("testsModule.editor.points", {
                              defaultValue: "Points",
                            })}
                          />
                          <span>
                            {tFn("testsModule.editor.pointsAbbrev", {
                              defaultValue: "pts",
                            })}
                          </span>
                        </label>
                      ) : null}
                      <button
                        type="button"
                        className="test-editor__iconBtn"
                        onClick={() => moveExercise(exercise.id, -1)}
                        disabled={index === 0}
                        aria-label={tFn("testsModule.runner.moveUp", {
                          defaultValue: "Move up",
                        })}
                        title={tFn("testsModule.runner.moveUp", {
                          defaultValue: "Move up",
                        })}
                      >
                        <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="test-editor__iconBtn"
                        onClick={() => moveExercise(exercise.id, 1)}
                        disabled={index === sortedExercises.length - 1}
                        aria-label={tFn("testsModule.runner.moveDown", {
                          defaultValue: "Move down",
                        })}
                        title={tFn("testsModule.runner.moveDown", {
                          defaultValue: "Move down",
                        })}
                      >
                        <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="test-editor__iconBtn test-editor__iconBtn--danger"
                        onClick={() => deleteExercise(exercise.id)}
                        disabled={exerciseBusyId === exercise.id}
                        aria-label={tFn("testsModule.common.delete", {
                          defaultValue: "Delete",
                        })}
                        title={tFn("testsModule.common.delete", {
                          defaultValue: "Delete",
                        })}
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </header>

                  <label className="test-editor__full test-editor__prompt">
                    <span>
                      {isStimulus
                        ? tFn("testsModule.editor.stimulusText", {
                            defaultValue: "Text",
                          })
                        : tFn("testsModule.editor.questionText", {
                            defaultValue: "Question",
                          })}
                    </span>
                    <textarea
                      rows={isStimulus ? 6 : 2}
                      value={exercise.prompt || ""}
                      onChange={(e) =>
                        updateExercise({ ...exercise, prompt: e.target.value })
                      }
                    />
                  </label>

                  <ExerciseConfigEditor
                    exercise={exercise}
                    onChange={(next) => updateExercise(next)}
                  />

                  {problems.length ? (
                    <ul className="test-editor__warnings">
                      {problems.map((key) => (
                        <li key={key}>
                          <i
                            className="fa-solid fa-triangle-exclamation"
                            aria-hidden="true"
                          />
                          {tFn(`testsModule.editor.problems.${key}`, {
                            defaultValue: key,
                          })}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <details className="test-editor__preview">
                    <summary>
                      {tFn("testsModule.editor.livePreview", {
                        defaultValue: "Live preview",
                      })}
                    </summary>
                    <ExerciseLivePreview exercise={exercise} language={language} />
                  </details>
                </article>
              );
            })}

            {!sortedExercises.length ? (
              <div className="test-editor__empty">
                {tFn("testsModule.editor.noExercisesYet", {
                  defaultValue: "No exercises yet.",
                })}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </Wrapper>
  );
}

// Shared with the homework editor, which edits the same kinds of questions.
export {
  COMMON_EXERCISE_TYPES,
  COMMON_TYPE_VALUES,
  EXERCISE_TYPE_ICONS,
  EXERCISE_TYPES,
  ExerciseConfigEditor,
  ExerciseLivePreview,
  buildDefaultConfig,
  exerciseProblems,
};
